import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiDatabase,
  FiShield,
  FiServer,
  FiClock,
  FiSettings,
  FiCheckCircle,
  FiRefreshCw,
  FiFileText,
  FiArrowRight,
  FiMail,
  FiActivity,
} from 'react-icons/fi';
import AdminLayout from '../components/AdminLayout';
import {
  AdminPageHeader,
  AdminKpiGrid,
  AdminSection,
  AdminActivityList,
  pillClass,
} from '../components/admin';
import { FormAlert } from '../components/form';
import useAdminPermissions from '../hooks/useAdminPermissions';
import { useApi } from '../hooks/useApi';
import { useThemeClasses } from '../hooks/useThemeClasses';

const AdminSystem = () => {
  const tc = useThemeClasses();
  const { canManageSystem } = useAdminPermissions();
  const { secureFetch } = useApi();

  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [launchChecklist, setLaunchChecklist] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activityPage, setActivityPage] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [statsRes, healthRes, activitiesRes] = await Promise.all([
        secureFetch('/api/admin/dashboard/stats'),
        secureFetch('/api/admin/dashboard/health'),
        secureFetch('/api/admin/dashboard/activities'),
      ]);
      if (!statsRes.ok) {
        const data = await statsRes.json();
        throw new Error(data.message || 'Error al obtener estadísticas del sistema');
      }
      if (!activitiesRes.ok) {
        const data = await activitiesRes.json();
        throw new Error(data.message || 'Error al obtener actividad reciente');
      }
      setStats(await statsRes.json());
      if (healthRes.ok) {
        const healthJson = await healthRes.json();
        setHealth(healthJson);
        setLaunchChecklist(healthJson.launchChecklist || null);
      } else {
        setHealth(null);
        setLaunchChecklist(null);
      }
      setActivities((await activitiesRes.json()) || []);
      setActivityPage(1);
    } catch (err) {
      setError(err.message || 'Error al cargar datos del sistema.');
    } finally {
      setLoading(false);
    }
  }, [secureFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const systemHealth = health?.systemHealth || stats?.systemHealth || 'healthy';
  const healthTone = { healthy: 'success', warning: 'warning', critical: 'danger' }[systemHealth] || 'neutral';
  const healthLabel = { healthy: 'Saludable', warning: 'Advertencia', critical: 'Crítico' }[systemHealth] || 'Sin datos';

  const checks = health?.checks;
  const smtp = checks?.smtp;
  const mp = checks?.mercadopago;
  const dailyJobs = checks?.dailyJobs;
  const betaLaunch = checks?.betaLaunch;

  const kpis = [
    { icon: FiDatabase, label: 'Usuarios', value: stats ? stats.totalUsers : (loading ? '—' : 0), hint: 'Cuentas registradas' },
    { icon: FiShield, label: 'Empresas', value: stats ? stats.totalCompanies : (loading ? '—' : 0), hint: 'Organizaciones activas' },
    { icon: FiServer, label: 'Suscripciones', value: stats ? stats.activeSubscriptions : (loading ? '—' : 0), hint: 'Planes activos en la plataforma' },
    { icon: FiCheckCircle, label: 'Salud', value: healthLabel, hint: health?.timestamp ? `Revisado ${new Date(health.timestamp).toLocaleTimeString('es-AR')}` : 'Base de datos y servicios' },
  ];

  const StatusRow = ({ label, ok, detail, mono }) => (
    <div className={`flex items-center justify-between gap-3 p-2.5 rounded-lg ${tc.section}`}>
      <span>{label}</span>
      <div className="flex items-center gap-2 min-w-0">
        {mono && detail ? (
          <span className={`font-mono text-[10px] truncate max-w-[140px] ${tc.textPri}`} title={detail}>
            {detail}
          </span>
        ) : null}
        <span className={pillClass(tc.modoOscuro, ok ? 'success' : 'warning')}>
          {ok ? 'OK' : 'Revisar'}
        </span>
      </div>
    </div>
  );

  return (
    <AdminLayout requiredPermission={canManageSystem}>
      <div className="max-w-7xl mx-auto py-4 sm:py-6 space-y-5">
        <AdminPageHeader
          title="Sistema"
          subtitle="Estado operativo, integraciones y auditoría de plataforma."
          actions={
            <>
              <span className={pillClass(tc.modoOscuro, healthTone)}>
                <FiCheckCircle className="w-3 h-3" />
                {healthLabel}
              </span>
              <button type="button" onClick={fetchData} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${tc.btnOutline}`}>
                <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
            </>
          }
        />

        {error && <FormAlert type="error">{error}</FormAlert>}

        <AdminKpiGrid items={kpis} />

        {launchChecklist && (
          <AdminSection
            title="Checklist lanzamiento beta"
            description="Revisá estos puntos antes de abrir cupos al público"
            icon={FiActivity}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
              {[
                { label: 'SMTP configurado', ok: launchChecklist.smtpReady, detail: smtp?.from || 'Faltan SMTP_USER / SMTP_PASS' },
                { label: 'Base y planes listos', ok: launchChecklist.dbReady, detail: 'Supabase + migraciones + semilla' },
                { label: 'Mercado Pago', ok: launchChecklist.mpReady, detail: betaLaunch?.mercadopagoProductionToken ? 'Token producción' : 'Revisar token APP_USR' },
                { label: 'Cupo beta definido', ok: launchChecklist.betaConfigured, detail: betaLaunch ? `${betaLaunch.freeSlots} cupos · ${betaLaunch.freeDays} días` : '' },
                { label: 'Jobs diarios ejecutados', ok: launchChecklist.jobsRan, detail: dailyJobs?.lastCompletedAt ? new Date(dailyJobs.lastCompletedAt).toLocaleString('es-AR') : 'Reiniciá backend o esperá el ciclo horario' },
                { label: 'Jobs recientes (<48h)', ok: launchChecklist.jobsRecent, detail: dailyJobs?.schedulerNote },
              ].map((row) => (
                <StatusRow key={row.label} label={row.label} ok={row.ok} detail={row.detail} mono={Boolean(row.detail)} />
              ))}
            </div>
            {dailyJobs?.subscriptionLifecycle?.betaPro && (
              <p className={`text-[11px] mt-3 ${tc.textMuted}`}>
                Último ciclo beta: {dailyJobs.subscriptionLifecycle.betaPro.remindersSent ?? 0} aviso(s),{' '}
                {dailyJobs.subscriptionLifecycle.betaPro.expired ?? 0} vencimiento(s) procesados.
              </p>
            )}
          </AdminSection>
        )}

        <Link
          to="/admin/fiscal"
          className={`block rounded-2xl border p-4 sm:p-5 transition-colors ${tc.box} ${tc.modoOscuro ? 'hover:bg-gray-900/60' : 'hover:bg-white hover:shadow-sm'}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${tc.modoOscuro ? 'bg-gray-800 text-white' : 'bg-gray-900 text-white'}`}>
                <FiFileText className="w-4 h-4" />
              </div>
              <div>
                <p className={`text-sm font-semibold ${tc.textPri}`}>Parametría fiscal</p>
                <p className={`text-xs mt-1 ${tc.textSec}`}>
                  Topes y cuotas de Monotributo sin redeploy.
                </p>
              </div>
            </div>
            <FiArrowRight className={`w-4 h-4 shrink-0 mt-1 ${tc.textMuted}`} />
          </div>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <AdminSection title="Correo (SMTP)" description="Emails transaccionales y avisos beta" icon={FiMail}>
            <div className={`space-y-2 text-xs ${tc.textSec}`}>
              <StatusRow label="Configuración" ok={smtp?.configured} />
              <StatusRow label="Host" ok={Boolean(smtp?.host)} detail={smtp?.host} mono />
              <StatusRow label="Puerto" ok={Boolean(smtp?.port)} detail={String(smtp?.port || '—')} mono />
              <StatusRow label="Remitente" ok={Boolean(smtp?.from)} detail={smtp?.from || '—'} mono />
            </div>
          </AdminSection>

          <AdminSection title="Integraciones" description="Mercado Pago y base de datos" icon={FiSettings}>
            <div className={`space-y-2 text-xs ${tc.textSec}`}>
              <StatusRow label="Supabase" ok={checks?.supabase?.ok} />
              <StatusRow label="Migraciones" ok={checks?.migrations?.ok} detail={checks?.migrations?.missing?.length ? `Faltan: ${checks.migrations.missing.join(', ')}` : null} mono />
              <StatusRow label="Planes semilla" ok={checks?.plans?.ok} />
              <StatusRow label="Mercado Pago token" ok={mp?.configured} />
              <StatusRow label="Webhook MP" ok={Boolean(mp?.webhookUrl)} detail={mp?.webhookUrl} mono />
              <StatusRow
                label="FRONTEND_URL"
                ok={Boolean(checks?.env?.frontendUrlSet)}
                detail={checks?.env?.frontendUrlSet ? 'Definida' : 'Requerida para links en mails'}
              />
            </div>
          </AdminSection>
        </div>

        <AdminSection
          title="Actividad de plataforma"
          description="Altas admin, equipo y suscripciones (no operativa de clientes)"
          icon={FiClock}
          action={
            <Link
              to="/admin/activity"
              className={`text-[11px] inline-flex items-center gap-1 ${tc.textMuted} hover:text-[#1F80FF]`}
            >
              Ver listado completo
              <FiActivity className="w-3 h-3" />
            </Link>
          }
        >
          <AdminActivityList
            activities={activities}
            loading={loading && !activities.length}
            page={activityPage}
            pageSize={12}
            onPageChange={setActivityPage}
          />
        </AdminSection>
      </div>
    </AdminLayout>
  );
};

export default AdminSystem;
